import React, { useState, useEffect } from 'react';
import { useHRMS } from '../context/HRMSContext.jsx';
import api from '../services/api.js';
import DatePicker from '../components/DatePicker.jsx';
import Message from '../components/Message.jsx';
import PermissionGuard from '../components/PermissionGuard.jsx';
import { MODULES, ACTIONS } from '../context/PermissionsContext.jsx';
import CommonTable from '../components/common/CommonTable.jsx';

function HolidayMaster() {
  const { state, loadHolidays } = useHRMS();
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    holidayName: '',
    fromDate: '',
    toDate: '',
    createEveryYear: false
  });
  
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionLoadingText, setActionLoadingText] = useState("Processing...");
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

const [holidayFilters, setHolidayFilters] = useState({
  holidayName: "",
  fromDate: "",
  toDate: "",
});

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Validate dates
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    
    if (toDate < fromDate) {
      setMessage({ text: 'To date should be same or after from date!', type: 'error' });
      return;
    }
    
    // Check for duplicate holiday name
    const existingHoliday = (state.holidays || []).find(holiday => 
      holiday.holidayName.toLowerCase() === formData.holidayName.toLowerCase() &&
      (!editingHoliday || holiday.id !== editingHoliday.id)
    );
    
    if (existingHoliday) {
      setMessage({ text: 'Holiday name already exists!', type: 'error' });
      return;
    }
    // 🚨 Prevent duplicate / overlapping holiday dates
const isDateConflict = (state.holidays || []).some(holiday => {
  if (editingHoliday && holiday.id === editingHoliday.id) {
    return false; // allow editing same record
  }

  const existingFrom = new Date(holiday.fromDate);
  const existingTo = new Date(holiday.toDate);

  // Check overlap
  return (
    fromDate <= existingTo &&
    toDate >= existingFrom
  );
});

if (isDateConflict) {
  setMessage({
    text: "A holiday already exists for the selected date(s)!",
    type: "error"
  });
  return;
}
    try {
      setIsSubmitting(true);
      if (editingHoliday) {
        await api.holidays.update(editingHoliday.id, formData);
        setMessage({ text: 'Holiday updated successfully!', type: 'success' });
      } else {
        await api.holidays.create(formData);
        setMessage({ text: 'Holiday created successfully!', type: 'success' });
      }
      await loadHolidays();
      setTableRefreshKey((k) => k + 1);
      resetForm();
    } catch (error) {
      console.error('Error saving holiday:', error);
      setMessage({ text: error?.message || 'Error saving holiday!', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    if (isSubmitting) return;
    setFormData({
      holidayName: '',
      fromDate: '',
      toDate: '',
      createEveryYear: false
    });
    setShowModal(false);
    setEditingHoliday(null);
  };

  const handleEdit = async (holiday) => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setActionLoadingText("Loading holiday details...");
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setEditingHoliday(holiday);
      setFormData({
        holidayName: holiday.holidayName,
        fromDate: holiday.fromDate ? new Date(holiday.fromDate).toISOString().split('T')[0] : '',
        toDate: holiday.toDate ? new Date(holiday.toDate).toISOString().split('T')[0] : '',
        createEveryYear: holiday.createEveryYear
      });
      setShowModal(true);
    } catch (error) {
      console.error("Error opening edit holiday:", error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleInfo = (holiday) => {
    setSelectedHoliday(holiday);
    setShowInfoModal(true);
  };

  const handleToggleStatus = async (holiday) => {
    if (isActionLoading) return;
    const currentStatus = holiday.status ?? 1;
    const newStatus = currentStatus === 1 ? 2 : 1;
    const isActivating = newStatus === 1;
    const actionText = isActivating ? "activate" : "inactivate";
    const holidayName = holiday.holidayName || "this holiday";

    if (window.confirm(`Are you sure you want to ${actionText} ${holidayName}?`)) {
      setIsActionLoading(true);
      setActionLoadingText(`${isActivating ? "Activating" : "Inactivating"} holiday...`);
      try {
        await api.holidays.toggleStatus(holiday.id, newStatus);
        await loadHolidays();
        setTableRefreshKey((k) => k + 1);
        setMessage({
          text: `Holiday ${isActivating ? "activated" : "inactivated"} successfully!`,
          type: "success",
        });
      } catch (error) {
        console.error("Error toggling holiday status:", error);
        setMessage({
          text: error.message || `Failed to ${actionText} holiday`,
          type: "error",
        });
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (isActionLoading) return;
    if (window.confirm('Are you sure?')) {
      setIsActionLoading(true);
      setActionLoadingText("Deleting holiday...");
      try {
        await api.holidays.delete(id);
        await loadHolidays();
        setTableRefreshKey((k) => k + 1);
        setMessage({ text: 'Holiday deleted successfully!', type: 'success' });
      } catch (error) {
        console.error('Error deleting holiday:', error);
        setMessage({ text: 'Error deleting holiday!', type: 'error' });
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const calculateDays = (fromDate, toDate) => {
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };
const filteredHolidays = (state.holidays || []).filter((holiday) => {

  const matchesHolidayName =
    !holidayFilters.holidayName ||
    holiday.holidayName
      .toLowerCase()
      .includes(holidayFilters.holidayName.toLowerCase());

  const holidayFromDate = new Date(holiday.fromDate);
  const holidayToDate = new Date(holiday.toDate);

  const filterFromDate = holidayFilters.fromDate
    ? new Date(holidayFilters.fromDate)
    : null;

  const filterToDate = holidayFilters.toDate
    ? new Date(holidayFilters.toDate)
    : null;

  const matchesFromDate =
    !filterFromDate ||
    holidayFromDate >= filterFromDate;

  const matchesToDate =
    !filterToDate ||
    holidayToDate <= filterToDate;

  return (
    matchesHolidayName &&
    matchesFromDate &&
    matchesToDate
  );
});
  // Load holidays when component mounts
  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // Handle browser back button to close modal
  useEffect(() => {
    const handlePopState = (event) => {
      if (showModal) {
        event.preventDefault();
        event.stopPropagation();
        resetForm();
      }
    };

    if (showModal) {
      window.history.pushState({ page: 'holidays', modalOpen: true }, '', '#holidays-modal');
      window.addEventListener('popstate', handlePopState, true);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [showModal]);

  return (
    <PermissionGuard module={MODULES.HOLIDAYS} action={ACTIONS.VIEW} submodule={MODULES.HOLIDAYS.submodules.HOLIDAY_MASTER}>
      <div className="holiday-master">
        {/* Full-screen blocking loader when action is triggered */}
        {isActionLoading && (
          <div
            className="full-screen-action-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              inset: 0,
              width: "100%",
              height: "100%",
              minWidth: "100%",
              minHeight: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              zIndex: 999999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "all",
              cursor: "wait",
            }}
          >
            <div
              className="full-screen-action-card"
              style={{
                background: "#ffffff",
                padding: "18px 28px",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                pointerEvents: "auto",
              }}
            >
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "2rem", height: "2rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <span
                className="full-screen-action-text"
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#212529",
                  letterSpacing: "0.2px",
                }}
              >
                {actionLoadingText || "Processing..."}
              </span>
            </div>
          </div>
        )}

      {message.text && (
        <Message 
          message={message.text} 
          type={message.type} 
          onClose={() => setMessage({ text: '', type: '' })} 
        />
      )}
  <div className="d-flex justify-content-between align-items-center mb-1">

  <h2 className='hd_a'>Holiday Master</h2>

  <div className="d-flex align-items-center gap-2">

    <button
      type="button"
      className={`btn ${
        Object.values(holidayFilters).some(v => v !== "")
          ? "btn-primary"
          : "btn-outline-primary"
      }`}
      onClick={() => setShowFilters(!showFilters)}
    >
      <i className="bi bi-funnel"></i>
    </button>

    <PermissionGuard
      module={MODULES.HOLIDAYS}
      action={ACTIONS.CREATE}
      submodule={MODULES.HOLIDAYS.submodules.HOLIDAY_MASTER}
    >
      <button
        className="btn btn-primary"
        onClick={() => {
          setShowModal(true);
        }}
      >
        <i className="bi bi-plus-lg me-1"></i> Add Holiday
      </button>
    </PermissionGuard>

  </div>
</div>
        {/* <h2 className='hd_a'>Holiday Master</h2>
        <PermissionGuard module={MODULES.HOLIDAYS} action={ACTIONS.CREATE} submodule={MODULES.HOLIDAYS.submodules.HOLIDAY_MASTER}>
          <button className="btn btn-primary" onClick={() => {
            setShowModal(true);
          }}>
            <i className="bi bi-plus-lg me-1"></i> Add Holiday
          </button>
        </PermissionGuard>
      </div> */}
      {showFilters && (
  <div className="card shadow-sm mb-3">
    <div className="card-body py-2">
      <div className="row g-3">

        <div className="col-md-3">
          <label className="form-label">Holiday Name</label>

          <input
            type="text"
            className="form-control"
            placeholder="Search holiday"
            value={holidayFilters.holidayName}
            onChange={(e) =>
              setHolidayFilters({
                ...holidayFilters,
                holidayName: e.target.value,
              })
            }
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">From Date</label>

          <DatePicker
            value={holidayFilters.fromDate}
            onChange={(date) =>
              setHolidayFilters({
                ...holidayFilters,
                fromDate: date,
              })
            }
            placeholder="Select from date"
            className="form-control"
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">To Date</label>

          <DatePicker
            value={holidayFilters.toDate}
            onChange={(date) =>
              setHolidayFilters({
                ...holidayFilters,
                toDate: date,
              })
            }
            placeholder="Select to date"
            className="form-control"
          />
        </div>

        <div className="col-md-2 d-flex align-items-end">
          <button
            type="button"
            className="btn btn-outline-primary w-100"
            onClick={() =>
              setHolidayFilters({
                holidayName: "",
                fromDate: "",
                toDate: "",
              })
            }
          >
            Clear
          </button>
        </div>

      </div>
    </div>
  </div>
)}

      {showModal && (
        <div className="modal" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.3)', pointerEvents: isSubmitting ? 'none' : 'auto'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-sm border-0">
              <div className="modal-header border-bottom pt-2 pr-0 pb-2" style={{backgroundColor: 'var(--primary-light)'}}>
                <h6 className="modal-title" style={{color: 'var(--primary-color)'}}>{editingHoliday ? 'Edit' : 'Add'} Holiday</h6>
                <button type="button" className="btn-close" onClick={resetForm} disabled={isSubmitting}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-3">
                  <div className="row g-2">
                    <div className="col-4">
                      <label className="form-label small text-muted">Holiday Name *</label>
                      <input
                        type="text"
                        className="form-control form-control"
                        value={formData.holidayName}
                        onChange={(e) => setFormData({...formData, holidayName: e.target.value})}
                        disabled={isSubmitting}
                        required
                      />
                    </div>
                    <div className="col-4">
                      <label className="form-label small text-muted">From Date *</label>
                      <DatePicker
                        value={formData.fromDate}
                        onChange={(date) => setFormData({...formData, fromDate: date})}
                        placeholder="Select start date"
                        className="form-control form-control-sm"
                        disabled={isSubmitting}
                        required
                      />
                    </div>
                    <div className="col-4">
                      <label className="form-label small text-muted">To Date *</label>
                      <DatePicker
                        value={formData.toDate}
                        onChange={(date) => setFormData({...formData, toDate: date})}
                        placeholder="Select end date"
                        className="form-control form-control-sm"
                        minDate={formData.fromDate}
                        disabled={isSubmitting}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-check form-check-sm">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.createEveryYear}
                          onChange={(e) => setFormData({...formData, createEveryYear: e.target.checked})}
                          disabled={isSubmitting}
                        />
                        <label className="form-check-label small text-muted">
                          Create this Holiday for every year
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light py-2">
                  <button type="submit" className="btn btn-sm btn-primary" disabled={isSubmitting}>
                    {isSubmitting && (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    )}
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetForm} disabled={isSubmitting}>
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showInfoModal && selectedHoliday && (
        <div
          className="modal"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow border-0">
              <div
                className="modal-header border-bottom"
                style={{ backgroundColor: "var(--primary-light, #f8f9fa)" }}
              >
                <h6 className="modal-title m-0 text-primary fw-bold">
                  <i className="bi bi-info-circle-fill me-2"></i>Holiday Details
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowInfoModal(false);
                    setSelectedHoliday(null);
                  }}
                ></button>
              </div>
              <div className="modal-body p-3">
                <table className="table table-bordered table-sm mb-0">
                  <tbody>
                    <tr>
                      <th style={{ width: "40%", background: "#f8f9fa" }}>
                        Holiday Name
                      </th>
                      <td>{selectedHoliday.holidayName}</td>
                    </tr>
                    <tr>
                      <th style={{ background: "#f8f9fa" }}>Status</th>
                      <td>
                        {(selectedHoliday.status ?? 1) === 1 ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-danger">Inactive</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th style={{ background: "#f8f9fa" }}>From Date</th>
                      <td>{selectedHoliday.fromDate}</td>
                    </tr>
                    <tr>
                      <th style={{ background: "#f8f9fa" }}>To Date</th>
                      <td>{selectedHoliday.toDate}</td>
                    </tr>
                    <tr>
                      <th style={{ background: "#f8f9fa" }}>Total Days</th>
                      <td>
                        {calculateDays(
                          selectedHoliday.fromDate,
                          selectedHoliday.toDate
                        )}{" "}
                        days
                      </td>
                    </tr>
                    <tr>
                      <th style={{ background: "#f8f9fa" }}>Every Year</th>
                      <td>{selectedHoliday.createEveryYear ? "Yes" : "No"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer p-2">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    setShowInfoModal(false);
                    setSelectedHoliday(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body px-0 pt-0">
          <CommonTable
            key={tableRefreshKey}
            dataSource={api.holidays.table}
            queryParams={{ holidayName: holidayFilters.holidayName, fromDate: holidayFilters.fromDate, toDate: holidayFilters.toDate }}
            columns={[
              { key: 'holidayName', label: 'Holiday Name', sortable: true },
              { key: 'fromDate', label: 'From Date', sortable: true },
              { key: 'toDate', label: 'To Date', sortable: true },
              { key: 'days', label: 'Days', sortable: false, render: (value, holiday) => calculateDays(holiday.fromDate, holiday.toDate) },
              { key: 'createEveryYear', label: 'Every Year', sortable: true, render: (holiday) => holiday.createEveryYear ? 'Yes' : 'No' },
              {
                key: 'status',
                label: 'Status',
                sortable: true,
                render: (value, holiday) => (
                  (holiday.status ?? 1) === 1 ? (
                    <span className="badge bg-success" style={{ fontSize: '11px', padding: '4px 8px' }}>Active</span>
                  ) : (
                    <span className="badge bg-danger" style={{ fontSize: '11px', padding: '4px 8px' }}>Inactive</span>
                  )
                )
              }
            ]}
            renderActions={(holiday) => {
              const isActive = (holiday.status ?? 1) === 1;
              return (
                <div className="d-flex align-items-center gap-1">
                  <button
                    title="View Details"
                    className="btn btn-sm btn-primary"
                    disabled={isActionLoading}
                    onClick={() => handleInfo(holiday)}
                    style={{ fontSize: '13px', padding: '4px 10px' }}
                  >
                    <i className="bi bi-info-circle-fill"></i>
                  </button>
                  {isActive && (
                    <PermissionGuard module={MODULES.HOLIDAYS} action={ACTIONS.EDIT} submodule={MODULES.HOLIDAYS.submodules.HOLIDAY_MASTER}>
                      <button
                        title='Edit'
                        className="btn btn-sm btn-primary"
                        disabled={isActionLoading}
                        onClick={() => handleEdit(holiday)}
                        style={{ fontSize: '13px', padding: '4px 10px' }}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>
                    </PermissionGuard>
                  )}
                  {/* Hard delete commented out as per soft delete requirement
                  <PermissionGuard module={MODULES.HOLIDAYS} action={ACTIONS.DELETE} submodule={MODULES.HOLIDAYS.submodules.HOLIDAY_MASTER}>
                    <button
                      title='Delete'
                      className="btn btn-sm btn-danger"
                      disabled={isActionLoading}
                      onClick={() => handleDelete(holiday.id)}
                      style={{ fontSize: '13px', padding: '4px 10px' }}
                    >
                      <i className="bi bi-trash-fill"></i>
                    </button>
                  </PermissionGuard>
                  */}
                  <button
                    title={isActive ? "Inactivate Holiday" : "Activate Holiday"}
                    className={`btn btn-sm ${isActive ? "btn-outline-danger" : "btn-outline-success"}`}
                    onClick={() => handleToggleStatus(holiday)}
                    disabled={isActionLoading}
                    style={{ fontSize: "13px", padding: "4px 10px" }}
                  >
                    {isActive ? (
                      <i className="bi bi-slash-circle" title="Inactivate"></i>
                    ) : (
                      <i className="bi bi-check-circle" title="Activate"></i>
                    )}
                  </button>
                </div>
              );
            }}
          />
        </div>
      </div>
      </div>
    </PermissionGuard>
  );
}

export default HolidayMaster;